import { api } from '@/lib/api'

export type UserRole = 'admin' | 'project_manager' | 'standard_staff' | 'scheduling_team' | 'sales_team' | 'client'

export type Discipline = 'pentest' | 'vmaas' | ''

export interface User {
  id: number
  public_id?: string
  email: string
  first_name: string
  last_name: string
  user_type: 'admin' | 'consultant' | 'client'
  is_staff: boolean
  is_superuser: boolean
  staff_role: UserRole | null
  is_active: boolean
  is_verified: boolean
  client_organization?: number | null
  discipline?: Discipline
  company?: string
  phone?: string
  profile_picture?: string
  module_access?: Record<string, boolean>
}

export type ActiveClient = {
  id: number
  name: string
  public_id?: string | null
} | null

export interface Me extends User {
  authenticated?: boolean
  active_client_organization_id?: number | null
  active_client?: ActiveClient
  has_asm_portfolios?: boolean
}

export interface UserInviteData {
  email: string
  first_name?: string
  last_name?: string
  role: UserRole
  discipline?: Discipline
  client_organization?: number | null
  client_access_ids?: number[]
  company?: string
  phone?: string
}

export type UserClientAccess = {
  readonly user_id: number
  readonly client_organization_ids: number[]
}

export interface UserActivationData {
  uid: string
  token: string
  password: string
  confirm_password: string
}

export interface UserActivationResult extends User {
  portfolio_mfa_required?: boolean
  methods?: Array<'totp' | 'email'>
  preauth?: string
  forced_email_mfa?: boolean
}

// Current user
export async function getMe() {
  if (typeof window !== 'undefined') {
    const path = window.location?.pathname || ''
    if (path === '/' || path === '/login' || path === '/twofa' || path === '/activate' || path === '/reset-password') {
      const err: any = new Error('unauth (public route)')
      err.response = { status: 403, statusText: 'Forbidden' }
      err.config = { url: '/api/auth/me/' }
      throw err
    }
  }
  const { data } = await api.get<Me>('/api/auth/me/')
  return data
}

export async function updateMe(profileData: { first_name?: string; last_name?: string }) {
  const { data } = await api.patch<Me>('/api/auth/me/', profileData)
  return data
}

export async function requestOwnPasswordReset() {
  const { data } = await api.post<{ detail?: string }>('/api/auth/me/password-reset/', {})
  return data
}

export async function confirmPasswordReset(resetData: { token: string; password: string; confirm_password: string }) {
  const { data } = await api.post<{ detail?: string }>('/api/auth/password-reset/confirm/', resetData)
  return data
}

// User management (admin only)
type Paginated<T> = {
  count?: number
  next?: string | null
  previous?: string | null
  results?: T[]
  items?: T[]
}

export async function getUsers() {
  const { data } = await api.get<Paginated<User> | User[]>('/api/auth/users/')
  if (Array.isArray(data)) return data
  if (data && Array.isArray((data as Paginated<User>).results)) return (data as Paginated<User>).results as User[]
  if (data && Array.isArray((data as Paginated<User>).items)) return (data as Paginated<User>).items as User[]
  return []
}

export async function getUsersPaginated(params: { page?: number; page_size?: number; search?: string } = {}) {
  const { data } = await api.get<Paginated<User> | User[]>('/api/auth/users/', { params })
  if (Array.isArray(data)) {
    return { items: data, count: data.length }
  }
  const pg = data as Paginated<User>
  const items = (pg.results || pg.items || []) as User[]
  const count = typeof pg.count === 'number' ? pg.count : items.length
  return { items, count }
}

export async function getUser(id: number) {
  const { data } = await api.get<User>(`/api/auth/users/${id}/`)
  return data
}

interface UserUpdateData extends Omit<Partial<User>, 'staff_role'> {
  role?: UserRole;
}

export async function updateUser(id: number, userData: UserUpdateData) {
  const { role, ...rest } = userData;
  const payload = { ...rest } as Record<string, unknown>;
  
  if (role) {
    payload.role = role;
  }
  
  const { data } = await api.patch<User>(`/api/auth/users/${id}/`, payload);
  return data;
}

export async function deleteUser(id: number) {
  await api.delete(`/api/auth/users/${id}/`)
}

export async function resendActivation(id: number) {
  const { data } = await api.post<{ detail: string }>(`/api/auth/users/${id}/resend-activation/`, {})
  return data
}

// Invitations
export async function inviteUser(inviteData: UserInviteData) {
  const { data } = await api.post<User>('/api/auth/users/invite/', inviteData)
  return data
}

export async function getUserClientAccess(id: number) {
  const { data } = await api.get<UserClientAccess>(`/api/auth/users/${id}/client-access/`)
  return data
}

export async function setUserClientAccess(id: number, clientOrganizationIds: readonly number[]) {
  const { data } = await api.put<UserClientAccess>(`/api/auth/users/${id}/client-access/`, {
    client_organization_ids: clientOrganizationIds,
  })
  return data
}

export async function getUserModuleAccess(userId: number) {
  const { data } = await api.get<{ modules: Record<string, boolean> }>(`/api/auth/admin/users/${userId}/modules/`)
  return data.modules
}

export async function setUserModuleAccess(userId: number, modules: Record<string, boolean>) {
  const { data } = await api.patch<{ modules: Record<string, boolean> }>(`/api/auth/admin/users/${userId}/modules/`, { modules })
  return data.modules
}

// Account activation
export async function activateUser(activationData: UserActivationData) {
  const { data } = await api.post<UserActivationResult>('/api/auth/activate/', activationData)
  return data
}

// Role helpers
export function getUserRole(user: User): UserRole {
  if (user.is_superuser) return 'admin'
  if (user.user_type === 'client' && !user.is_staff) return 'client'
  return user.staff_role || 'standard_staff'
}

export function hasRole(user: User, role: UserRole): boolean {
  return getUserRole(user) === role
}

export function hasAnyRole(user: User, roles: UserRole[]): boolean {
  return roles.includes(getUserRole(user))
}

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  project_manager: 'Project Manager',
  standard_staff: 'Standard Staff',
  scheduling_team: 'Scheduling Team',
  sales_team: 'Sales Team',
  client: 'Client',
}

export async function requestPasswordReset(id: number) {
  const { data } = await api.post<{ detail?: string }>(`/api/auth/users/${id}/password-reset/`, {})
  return data
}

export async function resetUserTwoFA(id: number) {
  const { data } = await api.post<{ detail: string; devices_deleted: number }>(`/api/auth/users/${id}/2fa-reset/`, {})
  return data
}

export type AccountAuditLogItem = {
  readonly id: number
  readonly created_at: string
  readonly action: string
  readonly metadata: Record<string, unknown>
  readonly actor_email: string | null
  readonly target_email: string | null
}

type PaginatedAuditLog = {
  count?: number
  next?: string | null
  previous?: string | null
  results?: AccountAuditLogItem[]
  items?: AccountAuditLogItem[]
}

export async function getAccountAuditLog(params: { page?: number; page_size?: number } = {}) {
  const { data } = await api.get<PaginatedAuditLog | AccountAuditLogItem[]>('/api/auth/accounts/audit-log/', { params })
  if (Array.isArray(data)) return { items: data, count: data.length }
  const pg = data as PaginatedAuditLog
  const items = (pg.results || pg.items || []) as AccountAuditLogItem[]
  const count = typeof pg.count === 'number' ? pg.count : items.length
  return { items, count }
}
